import React, { FC, Fragment, useMemo, memo } from 'react';
import { Alert } from 'react-bootstrap';

import Link from 'next/link';

import ProductInfoComponent from '@views/components/product/ProductInfo';
import ProductName from '@views/components/product/ProductName';
import ProductPrice from '@views/components/product/ProductPrice';
import ProductRating from '@views/components/product/ProductRating';
import Rating from '@views/components/rating';
import Action from './action';
import Sidebar from './sidebar';

import { CONTENT } from '../intl';
import { formatCurrencyVN as formatCurrency } from '@helpers/format-currency';
import _percentage from '@helpers/percentage';
import _isContactPrice from '@helpers/is-contact-price';
import _get from 'lodash/get';
import styled from 'styled-components';

import toUrl from '@helpers/convert-to-url';
import brandCache from '@cache/brand';
import { INTERNAL_URI, brandHref } from '@helpers/route';

import cn from 'classnames';
import {
  EVENT_CLASS_NAME,
  EVENT_ACTION_NAME,
  EVENT_CATEGORY,
  EVENT_LABEL,
} from '@helpers/events';

const BrandTitle = styled.h6``;

export type ProductInfoProps = {
  id: string | number;
  name?: string;
  price?: number;
  listPrice?: number;
  rating?: any;
  quantityAvailable?: number;
  isContactProduct?: boolean;
  brand?: {
    id: number;
    value: string;
  };
};

const ProductInfo: FC<ProductInfoProps> = (props) => {
  const {
    id,
    name,
    price,
    listPrice,
    rating,
    quantityAvailable,
    isContactProduct,
    brand,
  } = props;

  const showAction = quantityAvailable > 0;
  const contentPrice = useMemo(() => {
    const sale: number = !!listPrice ? listPrice - price : 0;

    if (quantityAvailable <= 0) {
      return (
        <ProductPrice.NewPrice>{CONTENT.OUT_OF_STOCK}</ProductPrice.NewPrice>
      );
    }

    if (isContactProduct) {
      return <ProductPrice.NewPrice>{CONTENT.CONTACT}</ProductPrice.NewPrice>;
    }

    return (
      <>
        <ProductPrice.NewPrice as="span">
          {formatCurrency(price)}
        </ProductPrice.NewPrice>
        {!!sale && (
          <>
            <ProductPrice.OldPrice as="span" className="mr-2">
              {formatCurrency(listPrice)}
            </ProductPrice.OldPrice>
            <ProductPrice.BadgeSale as="span">
              -{_percentage(price, listPrice)}%
            </ProductPrice.BadgeSale>
          </>
        )}
      </>
    );
  }, [isContactProduct, quantityAvailable, price, listPrice]);

  return (
    <ProductInfoComponent>
      {!!brand && (
        <BrandTitle>
          Thương hiệu:{' '}
          <Link
            href={INTERNAL_URI.BRAND}
            as={brandHref(
              _get(brand, 'id'),
              _get(brandCache[brand.id], 'slug', toUrl(_get(brand, 'value')))
            )}
          >
            <a
              className={cn(EVENT_CLASS_NAME)}
              data-category={EVENT_CATEGORY.BRAND}
              data-action={EVENT_ACTION_NAME}
              data-label={_get(brand, 'value')}
            >
              {_get(brand, 'value')}
            </a>
          </Link>
        </BrandTitle>
      )}
      <ProductName as="h1" className="mt-3 mb-3">
        {name}
      </ProductName>
      {rating && (
        <ProductRating>
          <Rating star={rating} />{' '}
          <span className="product-card__rating-legend">({rating})</span>
        </ProductRating>
      )}

      <ProductPrice>{contentPrice}</ProductPrice>
      {showAction && <Action id={id} />}
      {isContactProduct && (
        <Alert variant="warning">
          <small>
            <em>{CONTENT.CONTACT_PRICE_MESSAGE}</em>
          </small>
        </Alert>
      )}
      <Sidebar />
    </ProductInfoComponent>
  );
};

export default memo<ProductInfoProps>(ProductInfo);
